import featureAnnouncementContent from './featureAnnouncementContent';

const FEATURE_ANNOUNCEMENT_REQUIRED_FIELDS = ['id', 'title'];

const parseFrontmatterValue = (rawValue = '') => {
    const value = rawValue.trim();

    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith('\'') && value.endsWith('\''))) {
        return value.slice(1, -1);
    }

    if (value === 'true') return true;
    if (value === 'false') return false;

    return value;
};

const normalizeMarkdownAssetPath = (asset) => {
    if (typeof asset === 'string') {
        return asset;
    }

    if (asset && typeof asset.default === 'string') {
        return asset.default;
    }

    throw new Error('Unable to resolve feature announcement asset path');
};

const normalizeMarkdownSource = (asset) => {
    if (typeof asset === 'string') {
        return asset;
    }

    if (asset && typeof asset.default === 'string') {
        return asset.default;
    }

    throw new Error('Unable to resolve feature announcement markdown source');
};

const isMarkdownContent = (value = '') => value.trimStart().startsWith('---');

export const createFeatureAnnouncementContext = () => featureAnnouncementContent;

export const discoverFeatureAnnouncementAssetPaths = (context = createFeatureAnnouncementContext()) => {
    const uniqueAssetPaths = new Set();

    context.keys()
        .filter(key => key.startsWith('./'))
        .sort()
        .forEach((key) => {
            uniqueAssetPaths.add(normalizeMarkdownAssetPath(context(key)));
        });

    return [...uniqueAssetPaths];
};

export const parseFeatureAnnouncementFrontmatter = (markdown) => {
    const match = markdown.match(/^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n?([\s\S]*)$/);

    if (!match) {
        throw new Error('Feature announcement markdown is missing frontmatter');
    }

    const [, frontmatterBlock, content = ''] = match;
    const metadata = frontmatterBlock
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .reduce((accumulator, line) => {
            const separatorIndex = line.indexOf(':');

            if (separatorIndex === -1) {
                throw new Error(`Invalid frontmatter line: ${line}`);
            }

            const key = line.slice(0, separatorIndex).trim();
            const value = parseFrontmatterValue(line.slice(separatorIndex + 1));

            accumulator[key] = value;
            return accumulator;
        }, {});

    return {
        metadata,
        content: content.trim(),
    };
};

export const parseFeatureAnnouncementMarkdown = (markdown, contentPath) => {
    const { metadata, content } = parseFeatureAnnouncementFrontmatter(markdown);

    FEATURE_ANNOUNCEMENT_REQUIRED_FIELDS.forEach((field) => {
        if (!metadata[field]) {
            throw new Error(`Feature announcement ${contentPath} is missing required field: ${field}`);
        }
    });

    const publishedAt = metadata.publishedAt || metadata.date;
    if (!publishedAt) {
        throw new Error(`Feature announcement ${contentPath} is missing required field: publishedAt`);
    }

    if (!content) {
        throw new Error(`Feature announcement ${contentPath} must include markdown content`);
    }

    const ctaUrl = metadata.ctaUrl || metadata.ctaHref || '';
    const ctaLabel = metadata.ctaLabel || '';

    if ((ctaLabel && !ctaUrl) || (!ctaLabel && ctaUrl)) {
        throw new Error(`Feature announcement ${contentPath} must define both ctaLabel and ctaUrl together`);
    }

    const summary = metadata.summary
        || content
            .split('\n')
            .map((line) => line.trim())
            .find((line) => line && !line.startsWith('#') && !line.startsWith('-') && !line.startsWith('*'))
        || '';

    return {
        id: metadata.id,
        title: metadata.title,
        summary,
        publishedAt,
        content,
        contentPath,
        ctaLabel,
        ctaUrl,
        ctaExternal: Boolean(metadata.ctaExternal),
    };
};

export const buildFeatureAnnouncementRegistry = (entries = []) => {
    const seenIds = new Set();

    return [...entries]
        .sort((left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime())
        .map((entry) => {
            if (seenIds.has(entry.id)) {
                throw new Error(`Duplicate feature announcement id: ${entry.id}`);
            }

            seenIds.add(entry.id);
            return entry;
        });
};

export const buildFeatureAnnouncementRegistryFromContext = (context = createFeatureAnnouncementContext()) => {
    const seenModuleIds = new Set();
    const uniqueKeys = context.keys().filter((key) => {
        const moduleId = typeof context.resolve === 'function' ? context.resolve(key) : key;

        if (seenModuleIds.has(moduleId)) {
            return false;
        }

        seenModuleIds.add(moduleId);
        return true;
    });

    return buildFeatureAnnouncementRegistry(uniqueKeys
        .map((key) => {
            const markdownSource = normalizeMarkdownSource(context(key));
            if (!isMarkdownContent(markdownSource)) {
                return null;
            }

            return parseFeatureAnnouncementMarkdown(markdownSource, key);
        })
        .filter(Boolean));
};

export const resolveFeatureAnnouncementCtaUrl = (ctaUrl, { organisationId } = {}) => {
    if (!ctaUrl) {
        return '';
    }

    if (!organisationId) {
        return ctaUrl;
    }

    return ctaUrl.replaceAll(':organisationId', organisationId);
};

export const getFeatureAnnouncementSeenStorageKey = (organisationId, userId) => `teal-business:new-features:seen:${organisationId || 'unknown-org'}:${userId || 'unknown-user'}`;

export const parseSeenFeatureAnnouncementIds = (rawValue) => {
    if (!rawValue) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        return Array.isArray(parsedValue) ? parsedValue.filter(Boolean) : [];
    } catch (error) {
        return [];
    }
};

export const getUnseenFeatureAnnouncements = (announcements = [], seenIds = []) => {
    const seenIdSet = new Set(seenIds);
    return announcements.filter((announcement) => !seenIdSet.has(announcement.id));
};

export const markFeatureAnnouncementsAsSeen = (storageKey, announcementIds = [], storage = typeof window !== 'undefined' ? window.localStorage : null) => {
    if (!storageKey || !storage) {
        return;
    }

    const existingSeenIds = parseSeenFeatureAnnouncementIds(storage.getItem(storageKey));
    const nextSeenIds = [...new Set([...existingSeenIds, ...announcementIds])];

    storage.setItem(storageKey, JSON.stringify(nextSeenIds));
};

export const formatFeatureAnnouncementDate = (publishedAt) => new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
}).format(new Date(publishedAt));

export const featureAnnouncementRegistry = buildFeatureAnnouncementRegistryFromContext();

export const loadFeatureAnnouncements = async (options = {}) => {
    if (Array.isArray(options)) {
        return options;
    }

    const {
        entries,
        assetPaths = discoverFeatureAnnouncementAssetPaths(),
        fetchImpl,
    } = options;
    const effectiveFetchImpl = fetchImpl
        || (typeof window !== 'undefined' && typeof window.fetch === 'function' ? window.fetch.bind(window) : null);

    if (entries) {
        return entries;
    }

    if (!effectiveFetchImpl) {
        return featureAnnouncementRegistry;
    }

    const announcements = await Promise.all(assetPaths.map(async (contentPath) => {
        const response = await effectiveFetchImpl(contentPath);

        if (!response.ok) {
            throw new Error(`Unable to load feature announcement markdown from ${contentPath}`);
        }

        return parseFeatureAnnouncementMarkdown(await response.text(), contentPath);
    }));

    return buildFeatureAnnouncementRegistry(announcements);
};
